import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  UserPermission
} from '@globaleris/ui.library'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme
} from '@material-ui/core'
import { Pageview } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumEstadoCiclo } from '../../../../api'
import { enumToSelectOptionArray } from '../../../../helpers'
import { NonAuthRoutes } from '../../../../model'
import { getCustomerDomain } from '../../../../redux'
import { CycleExecutionModel, CycleExecutionWithInvoiceCycleModel } from '../../invoiceCycles.model'

interface ExecutionHistoryDialogProps {
  open: boolean
  cycleName: string
  historyData: CycleExecutionModel[]
  isLoading: boolean
  onClose: () => void
}

export const ExecutionHistoryDialog: React.FC<ExecutionHistoryDialogProps> = ({
  open,
  cycleName,
  historyData,
  isLoading,
  onClose
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'invoiceCyclesPage'])
  const history = useHistory()
  const currentTheme = useTheme()

  const onViewDetailsActionHandler = (row: CycleExecutionWithInvoiceCycleModel) => {
    if (!row || !row.id) {
      return
    }

    history.push(NonAuthRoutes.invoiceCycleFinished, {
      executionCycleId: row.id,
      invoiceCycleId: row.invoiceCycleId,
      invoiceCycleType: row.invoiceCycleType,
      subjectType: row.invoiceCycleSubjectType,
      billingType: row.invoiceCycleBillingType,
      finishedExecutionCycle: true
    })
  }

  const historyColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'executionDate',
      label: t('invoiceCyclesPage:allCyclesTable.historyDialog.columns.executionDate'),
      type: DataType.DATE
    },
    {
      name: 'dateFrom',
      label: t('invoiceCyclesPage:allCyclesTable.historyDialog.columns.dateFrom'),
      type: DataType.DATE
    },
    {
      name: 'dateTo',
      label: t('invoiceCyclesPage:allCyclesTable.historyDialog.columns.dateTo'),
      type: DataType.DATE
    },
    {
      name: 'status',
      label: t('invoiceCyclesPage:allCyclesTable.historyDialog.columns.status'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoCiclo, 'enums:EnumEstadoCiclo')
    }
  ]

  const historyTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    filterType: FilterType.ADVANCED,
    elevation: 0,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    customInsertEditModal: true,
    pagination: true,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 20],
    analytics: {
      customer: getCustomerDomain(),
      view: 'Invoice Cycle History Table'
    },
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('invoiceCyclesPage:allCyclesTable.historyDialog.customActions.viewDetails'),
        onClick: row => onViewDetailsActionHandler(row),
        userPermission: UserPermission.EDIT
      }
    ]
  }

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle
        disableTypography
        style={{ padding: currentTheme.spacing(4), paddingTop: currentTheme.spacing(3) }}
      >
        <Typography variant="h1" color="primary" noWrap>
          {`Historial de ejecuciones - ${cycleName}`}
        </Typography>
        <Typography variant="subtitle1" color="secondary" noWrap>
          {t('invoiceCyclesPage:allCyclesTable.historyDialog.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: currentTheme.spacing(4), paddingTop: 0 }}>
        {isLoading ? (
          <DataTableSkeleton rows={5} elevation={0} />
        ) : (
          <DataTable data={historyData} columns={historyColumns} options={historyTableOptions} />
        )}
      </DialogContent>
      <DialogActions style={{ padding: currentTheme.spacing(2) }}>
        <Button size="small" color="primary" onClick={onClose}>
          {t('invoiceCyclesPage:buttons.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
